import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  FileText, 
  Download, 
  Plus, 
  Eye,
  DollarSign,
  Calendar,
  Building,
  Mail
} from 'lucide-react';
import { format } from 'date-fns';

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    start_date: '',
    end_date: '',
    hourly_rate: '',
    client_name: '',
    client_email: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchProjects();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/invoices');
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects?page_size=100');
      const { results: projects } = response.data;
      setProjects(projects);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setCreatingInvoice(true);
    try {
      const invoiceData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
      };

      await axios.post('/api/invoices', invoiceData);
      toast.success('Invoice created successfully');
      fetchInvoices();
      setShowCreateModal(false);
      setFormData({
        project_id: '',
        start_date: '',
        end_date: '',
        hourly_rate: '',
        client_name: '',
        client_email: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const downloadInvoicePDF = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getProjectName = (projectId) => {
    if (!Array.isArray(projects)) return 'Unknown Project';
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown Project';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="text-2xl font-bold text-secondary-900">Invoices</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Invoice
          </button>
        </div>

        {/* Invoices List */}
        <div className="card">
          {invoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">No invoices yet</h3>
              <p className="text-secondary-600 mb-4">Create your first invoice from billable hours</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Invoice #</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Client</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Project</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Period</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Hours</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(invoices) && invoices.map(invoice => (
                    <tr key={invoice.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                      <td className="py-3 px-4 text-sm font-medium">
                        {invoice.invoice_number}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {invoice.client_name || 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {getProjectName(invoice.project_id)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {format(new Date(invoice.start_date), 'MMM d')} - 
                        {format(new Date(invoice.end_date), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {invoice.total_hours?.toFixed(1)}h
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">
                        ${invoice.total_amount?.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          invoice.status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : invoice.status === 'sent'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedInvoice(invoice)}
                            className="p-1 text-secondary-400 hover:text-primary-600 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => downloadInvoicePDF(invoice)}
                            className="p-1 text-secondary-400 hover:text-green-600 transition-colors"
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Create New Invoice</h3>
            
            <form onSubmit={handleCreateInvoice} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Project *
                </label>
                <select
                  className="input-field"
                  value={formData.project_id}
                  onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                  required
                >
                  <option value="">Select a project</option>
                  {Array.isArray(projects) && projects.filter(p => p.is_billable).map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    className="input-field min-h-[44px]"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    className="input-field min-h-[44px]"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Hourly Rate ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                  placeholder="Use project rate if empty"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Client Name
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  placeholder="Use project client if empty"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Client Email
                </label>
                <input
                  type="email"
                  className="input-field"
                  value={formData.client_email}
                  onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={creatingInvoice}
                  className="btn-primary flex items-center gap-2"
                >
                  {creatingInvoice ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Invoice'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Invoice Details</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="border-b border-secondary-200 pb-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-secondary-900">INVOICE</h2>
                    <p className="text-secondary-600">#{selectedInvoice.invoice_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-secondary-600">Created</p>
                    <p className="font-medium">{format(new Date(selectedInvoice.created_at), 'MMM d, yyyy')}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                  <div>
                    <h4 className="font-medium text-secondary-900 mb-2">Bill To:</h4>
                    <p className="text-secondary-700">{selectedInvoice.client_name || 'N/A'}</p>
                    {selectedInvoice.client_email && (
                      <p className="text-secondary-600 text-sm">{selectedInvoice.client_email}</p>
                    )}
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-secondary-900 mb-2">Project:</h4>
                    <p className="text-secondary-700">{getProjectName(selectedInvoice.project_id)}</p>
                    <p className="text-secondary-600 text-sm">
                      {format(new Date(selectedInvoice.start_date), 'MMM d')} - 
                      {format(new Date(selectedInvoice.end_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Invoice Summary */}
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-secondary-600">Total Hours</p>
                    <p className="text-lg font-semibold text-secondary-900">
                      {selectedInvoice.total_hours?.toFixed(1)}h
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-600">Hourly Rate</p>
                    <p className="text-lg font-semibold text-secondary-900">
                      ${selectedInvoice.hourly_rate?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary-600">Total Amount</p>
                    <p className="text-xl font-bold text-primary-600">
                      ${selectedInvoice.total_amount?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => downloadInvoicePDF(selectedInvoice)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvoicesPage;